import { effectScope, EffectScope } from "vue";
import { tryOnScopeDispose } from "@vueuse/core";
import { values } from "lodash-es";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFn = (...args: any[]) => any;
type ResolverFn<Fn extends AnyFn> = (...args: Parameters<Fn>) => string;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultArgumentResolver = (...args: any[]) => values(args).join("_");

/**
 * Make a composable function usable with multiple Vue instances.
 * Depending on parameter, returns the used function.
 *
 * @see https://vueuse.org/createSharedComposable
 */
export default function createSharedStatefulComposable<Fn extends AnyFn>(
  composable: Fn,
  memoizeResolver: ResolverFn<Fn> = defaultArgumentResolver
): Fn {
  const state: Record<
    string,
    { subscribers: number; scope: EffectScope; value: ReturnType<Fn> }
  > = {};

  return <Fn>((...args: Parameters<Fn>) => {
    const key = memoizeResolver(...args);

    if (!state[key]) {
      const scope = effectScope(true);
      const value = scope.run(() => composable(...args));
      state[key] = { subscribers: 1, scope, value };
    } else {
      state[key].subscribers += 1;
    }

    tryOnScopeDispose(() => {
      state[key].subscribers -= 1;

      const { scope, subscribers } = state[key];
      if (scope && subscribers <= 0) {
        scope.stop();
        delete state[key];
      }
    });

    return state[key].value;
  });
}
